/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import React from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import Picture from './Picture';

import {
  Box,
  Link,
  SanityRichText,
  SxStyleProp,
  Text,
  Flex,
  IntrinsicImage,
} from '.';

import type { Block } from '@sanity/block-content-to-react';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

export type CTA = {
  image: {
    alt: string;
    src: string;
  };
  link: {
    url: string;
    label: string;
  };
};

export interface CrossLinkProps {
  title: string;
  imageSrc?: string;
  gatsbyImageData?: IGatsbyImageData;
  alt: string;
  caption: string | Block | Block[];
  link: {
    url: string;
    label: string;
  };
  ctas?: CTA[];
  sx?: ThemeUIStyleObject;
  className?: string;
}

export const CrossLink = ({
  title,
  imageSrc,
  gatsbyImageData,
  alt,
  caption,
  link,
  ctas,
}: CrossLinkProps): JSX.Element => {
  return (
    <Box
      data-module="CrossLink"
      sx={{
        position: 'relative',
        my: [4, null, 5],
        ml: [0, null, 6],
        mr: [0, null, 3],
        width: '100%',
      }}
    >
      <H sx={{ variant: 'styles.h3', fontFamily: 'headingSecondary', mb: 4 }}>
        {title}
      </H>
      <Box
        sx={{
          maxWidth: ['unset', null, 320, 425],
        }}
      >
        <Box
          sx={{
            width: ['unset', null, 320, 425],
            height: ['unset', null, 203, 270],
            overflow: 'hidden',
          }}
        >
          {imageSrc && (
            <Picture
              alt={alt}
              objectFit="cover"
              objectPosition="center"
              src={imageSrc}
            />
          )}
          {gatsbyImageData && (
            <IntrinsicImage
              alt={alt}
              image={gatsbyImageData}
              style={{ width: '100%', height: '100%', objectFit: 'fill' }}
            />
          )}
        </Box>
        {typeof caption === 'string' ? (
          <Text mb={4} mt={3}>
            {caption}
          </Text>
        ) : (
          <SanityRichText
            blocks={caption}
            sx={{ 'mb': 4, 'mt': 3, 'p:last-child': { mb: 0 } }}
          />
        )}
        {link.url && (
          <Link href={link.url} variant="underline">
            {link.label}
          </Link>
        )}
        {ctas && ctas.length && (
          <Flex
            sx={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              columnGap: 15,
            }}
          >
            {ctas.map((cta, index) => (
              <Link
                key={`crosslinkCTA${index}`}
                sx={{ border: '1px solid black' }}
                to={cta.link.url}
              >
                <Picture
                  alt="Download the app from the apple app store"
                  src={cta.image?.src}
                />
              </Link>
            ))}
          </Flex>
        )}
      </Box>
    </Box>
  );
};
